/**
 * "Estado de la conversación" (mockup Vista People): barras horizontales por
 * estado del proceso con conteos, sobre las filas filtradas. Los estados
 * "en curso" se muestran desagregados (en curso / pausada / pendiente de
 * validación / reabierta) solo cuando tienen personas.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { DTALK_STATUS, DTALK_DISPLAY_OPTIONS } from './dtalkShared';
import { dtalkDisplayStatus } from '@/../../shared/dtalk-stats';
import type { DTalkStatsRow } from '@/../../shared/dtalk-stats';

// Los 4 estados visibles (Flor 07/10/2026), en orden sin iniciar → completada.
const ORDER = DTALK_DISPLAY_OPTIONS;

// Tailwind bg del dot → color de barra (mismo vocabulario visual que los badges)
const BAR_TONE: Record<string, string> = {
  not_started: 'bg-slate-400',
  in_progress: 'bg-blue-500',
  pending_validation: 'bg-amber-500',
  completed: 'bg-emerald-500',
};

export function DTalkStatusBars({ rows }: { rows: DTalkStatsRow[] }) {
  const { t } = useTranslation();
  const convocadas = rows.filter((r) => r.eligibilityStatus === 'eligible');
  const total = convocadas.length;

  const counts = ORDER.map((status) => ({
    status,
    n: convocadas.filter((r) => dtalkDisplayStatus(r.processStatus) === status).length,
  }));

  return (
    <Card>
      <CardContent className="py-4">
        <h3 className="mb-3 text-sm font-semibold">{t('performance.dtalkstatusbars.estado_de_la_conversacion')}</h3>
        <div className="space-y-3">
          {counts.map(({ status, n }) => {
            const pct = total > 0 ? Math.round((n / total) * 100) : 0;
            return (
              <div key={status} className="flex items-center gap-3 text-sm">
                <span className="w-44 shrink-0 truncate text-muted-foreground">{t(DTALK_STATUS[status].labelKey)}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className={`h-full rounded-full ${BAR_TONE[status]}`} style={{ width: `${Math.max(n > 0 ? 2 : 0, pct)}%` }} />
                </div>
                <span className="w-20 shrink-0 text-right text-xs text-muted-foreground">
                  <b className="text-foreground">{n}</b> ({pct}%)
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{t('performance.dtalkstatusbars.base_convocadas', { total })}</p>
      </CardContent>
    </Card>
  );
}
